import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { Check, FileText, ChevronRight, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

interface SolicitudResumen {
  id: string;
  folio: string;
  titularRazonSocial: string;
  tramitadorRazonSocial: string | null;
  servicioCodigo: string | null;
  productoNombre: string;
  marca: string;
  estadoCodigo: string | null;
  fechaCreacion: string;
}

/** Confirmación post-creación de una Solicitud de Seguridad (equivalente al success del portlet LG). */
export function SolicitudSeguridadSuccessPage() {
  const { id } = useParams<{ id: string }>();

  const q = useQuery<SolicitudResumen>({
    queryKey: ["solicitud-seguridad-resumen", id],
    queryFn: async () =>
      (await api.get<SolicitudResumen>(`/api/sector/seguridad/solicitudes/${id}`)).data,
    enabled: !!id,
  });

  if (q.isLoading) {
    return (
      <div className="flex items-center justify-center py-20 text-fg-subtle">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Cargando solicitud…
      </div>
    );
  }
  if (q.error || !q.data) {
    return (
      <div className="rounded-lg border bg-estado-warn-bg p-4 text-sm text-estado-warn">
        No se pudo cargar el resumen de la solicitud.
      </div>
    );
  }

  const s = q.data;

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <section className="rounded-lg border-2 border-estado-ok/25 bg-estado-ok-bg p-6 shadow-card">
        <div className="flex items-start gap-3">
          <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-estado-ok text-white">
            <Check className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <h1 className="text-xl font-semibold text-estado-ok">Solicitud registrada correctamente</h1>
            <p className="mt-1 text-sm text-estado-ok">
              Tu solicitud quedó en revisión por el equipo de Analistas. Recibirás un correo
              en cada cambio de estado del trámite.
            </p>
          </div>
        </div>

        <dl className="mt-5 grid gap-3 sm:grid-cols-2">
          <Field label="Folio" value={<span className="font-mono text-base font-semibold text-fg">{s.folio}</span>} />
          <Field label="Estado" value={s.estadoCodigo ?? "—"} />
          <Field label="Fecha de solicitud" value={formatFecha(s.fechaCreacion)} />
          <Field label="Tipo de trámite" value={s.servicioCodigo ?? "—"} />
        </dl>
      </section>

      <section className="rounded-lg border bg-surface p-4 shadow-card">
        <h2 className="border-b pb-2 text-xs font-semibold uppercase tracking-wide text-fg-subtle">Resumen</h2>
        <dl className="mt-3 grid gap-3 sm:grid-cols-2">
          <Field label="Titular" value={s.titularRazonSocial} />
          {s.tramitadorRazonSocial && <Field label="Tramitador externo" value={s.tramitadorRazonSocial} />}
          <Field label="Producto" value={s.productoNombre} />
          <Field label="Marca" value={s.marca} />
        </dl>
      </section>

      <div className="flex justify-between gap-2">
        <Link
          to="/seguridad"
          className="inline-flex items-center gap-1 rounded border border-primary-strong px-4 py-1.5 text-sm font-medium text-primary-strong transition hover:bg-primary-tint"
        >
          <Check className="h-4 w-4" /> Aceptar (volver a Seguridad)
        </Link>
        <Link
          to={`/sector/seguridad/solicitudes/${s.id}`}
          className="inline-flex items-center gap-1 rounded bg-primary px-4 py-1.5 text-sm font-medium text-white transition hover:bg-primary-hover"
        >
          <FileText className="h-4 w-4" /> Ver detalle
          <ChevronRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-fg-subtle">{label}</dt>
      <dd className="mt-0.5 text-sm text-fg">{value}</dd>
    </div>
  );
}

function formatFecha(s: string): string {
  try {
    return new Date(s).toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return s;
  }
}
